import {
  UserListEntity,
  AxiosAPIError,
  UserUpdateValidator,
  profileKeys,
  usersKeys,
  FanEntity,
  FanValidator,
  PasswordValidator,
  PhoneValidator,
  EmailValidator,
  SESSION_STORAGE_VERIFICATION,
  VerifyCodeValidator,
} from '@seaters/constants';

import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  updateUser,
  fetchFan,
  updateFan,
  updateFanPassword,
  updateFanPhone,
  updateFanEmail,
  scheduleFanDeletion,
  verifyPhoneBySms,
} from '../api/users';
import {
  isVersionConflict,
  invalidateVersionedQueries,
  useVersionedSnapshot,
  withExpectedVersion,
} from '../utils/versionedUpdate';

export const useUserProfile = (enabled: boolean) => {
  return useQuery<FanEntity, Error>({
    queryKey: profileKeys.fan(),
    queryFn: () => fetchFan(),
    enabled: !!sessionStorage.getItem(SESSION_STORAGE_VERIFICATION) && enabled,
    // The email is usually confirmed in the tab the mail opens, then the fan comes back here. Only
    // an unconfirmed profile refetches on focus; the global default stays off (focus storms).
    refetchOnWindowFocus: (query) => query.state.data?.validatedEmail === false,
  });
};

export const useUpdateProfile = (
  userId: string,
  snapshotVersion?: number
): UseMutationResult<
  UserListEntity,
  AxiosAPIError<UserUpdateValidator>,
  UserUpdateValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  const cachedVersion = queryClient.getQueryData<UserListEntity>(
    usersKeys.detail(userId)
  )?.version;
  const { acceptServerVersion, getExpectedVersion } = useVersionedSnapshot(
    userId,
    snapshotVersion ?? cachedVersion
  );

  return useMutation({
    mutationKey: profileKeys.mutation(),
    mutationFn: (body: UserUpdateValidator) =>
      updateUser(userId, withExpectedVersion(body, getExpectedVersion())),
    onSuccess: (updatedUser) => {
      acceptServerVersion(updatedUser);
      queryClient.setQueryData(usersKeys.detail(userId), updatedUser);
    },
    onError: async (error) => {
      if (isVersionConflict(error)) {
        await invalidateVersionedQueries(queryClient, usersKeys.detail(userId));
      }
    },
  });
};

export const useUpdateFanProfile = (): UseMutationResult<
  FanEntity,
  AxiosAPIError<FanValidator>,
  FanValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: profileKeys.mutation(),
    mutationFn: (body: FanValidator) => updateFan(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.all() });
    },
  });
};

export const useUpdateFanPassword = (): UseMutationResult<
  FanEntity,
  AxiosAPIError<PasswordValidator>,
  PasswordValidator,
  unknown
> => {
  return useMutation({
    mutationKey: profileKeys.mutation(),
    mutationFn: (body: PasswordValidator) => updateFanPassword(body),
  });
};

export const useUpdateFanPhone = (): UseMutationResult<
  FanEntity,
  AxiosAPIError<PhoneValidator>,
  PhoneValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: profileKeys.mutation(),
    mutationFn: (body: PhoneValidator) => updateFanPhone(body),
    // the number lands unverified, so the unverified-phone banner has to see it right away
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.fan() });
    },
  });
};
export const useValidatePhoneCode = (): UseMutationResult<
  FanEntity,
  AxiosAPIError<VerifyCodeValidator>,
  VerifyCodeValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VerifyCodeValidator) => verifyPhoneBySms(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.fan() });
    },
  });
};

export const useUpdateFanEmail = (): UseMutationResult<
  FanEntity,
  AxiosAPIError<EmailValidator>,
  EmailValidator,
  unknown
> => {
  return useMutation({
    mutationKey: profileKeys.mutation(),
    mutationFn: (body: EmailValidator) => updateFanEmail(body),
  });
};

export const useScheduleFanDeletion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => scheduleFanDeletion(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.all() });
    },
  });
};
